import React, { useMemo, useState } from 'react';
import { describeLoadError, formatDateTime, plural, timeAgo, toDate } from './utils/visitorStatsHelpers';
import { ANON, UserBadge } from './VisitorStatsParts';
import './VisitorStatsTabs.css';

const PAGE_SIZE = 30;

const normalise = (event) => {
  const email = event.userEmail || null;
  const dancers = (Array.isArray(event.dancers) ? event.dancers : []).filter(Boolean);
  return {
    id: event.id,
    date: toDate(event.timestamp),
    dancers,
    results: typeof event.resultsCount === 'number' ? event.resultsCount : null,
    competitions: Array.isArray(event.competitions) ? event.competitions : null,
    user: email || event.userName ? { key: email || event.userId || event.userName, name: event.userName || email, email } : ANON,
  };
};

const pairsOf = (dancers) => {
  const pairs = [];
  for (let i = 0; i < dancers.length; i += 1) {
    for (let j = i + 1; j < dancers.length; j += 1) pairs.push([dancers[i], dancers[j]].sort().join(' vs '));
  }
  return pairs;
};

function ComparisonCard({ event }) {
  const { dancers, results, competitions, date, user } = event;
  return (
    <article className="vs-event">
      <header className="vs-event-head">
        <UserBadge user={user} />
        <time className="vs-event-time" title={formatDateTime(date)}>
          {formatDateTime(date)}
          <span>{timeAgo(date)}</span>
        </time>
      </header>

      <div className="vs-vs-row">
        {dancers.map((name, i) => (
          <React.Fragment key={`${name}-${i}`}>
            {i > 0 && <span className="vs-vs">vs</span>}
            <span className="vs-dancer">💃 {name}</span>
          </React.Fragment>
        ))}
      </div>

      <div className="vs-event-foot">
        {results === null ? (
          <span className="vs-badge vs-badge--muted">Result count not recorded</span>
        ) : results === 0 ? (
          <span className="vs-badge vs-badge--warn">No shared competitions</span>
        ) : (
          <span className="vs-badge vs-badge--ok">{plural(results, 'shared competition')}</span>
        )}
      </div>

      {competitions && competitions.length > 0 && (
        <details className="vs-details">
          <summary>See what they were shown</summary>
          <ul className="vs-comp-list">
            {competitions.map((c, i) => (
              <li key={i}>
                <div className="vs-comp-name">{c.name || 'Competition'}</div>
                <div className="vs-comp-meta">
                  {[c.category, c.date].filter(Boolean).join(' · ')}
                </div>
                {Array.isArray(c.placements) && c.placements.length > 0 && (
                  <div className="vs-placements">
                    {[...c.placements]
                      .sort((a, b) => (a.placement ?? 999) - (b.placement ?? 999))
                      .map((p, k) => (
                        <span key={k} className="vs-placement">
                          <b>{p.placement ?? '–'}</b> {p.name}
                        </span>
                      ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {results > competitions.length && <p className="vs-muted">Showing the first {competitions.length} of {results}.</p>}
        </details>
      )}
    </article>
  );
}

export default function VisitorComparisonsTab({ events, loading, error, onRetry }) {
  const [search, setSearch] = useState('');
  const [who, setWho] = useState('all');
  const [order, setOrder] = useState('newest');
  const [shown, setShown] = useState(PAGE_SIZE);

  const all = useMemo(() => events.map(normalise), [events]);

  const summary = useMemo(() => {
    const pairMap = {};
    const userMap = {};
    let anonymous = 0;
    let empty = 0;
    all.forEach((e) => {
      pairsOf(e.dancers).forEach((p) => {
        pairMap[p] = pairMap[p] || { count: 0, users: new Set() };
        pairMap[p].count += 1;
        pairMap[p].users.add(e.user.key);
      });
      if (e.user.key === ANON.key) anonymous += 1;
      else {
        userMap[e.user.key] = userMap[e.user.key] || { user: e.user, count: 0 };
        userMap[e.user.key].count += 1;
      }
      if (e.results === 0) empty += 1;
    });
    return {
      anonymous,
      empty,
      uniqueUsers: Object.keys(userMap).length,
      pairs: Object.keys(pairMap).length,
      topPairs: Object.entries(pairMap).sort((a, b) => b[1].count - a[1].count).slice(0, 10),
      topUsers: Object.values(userMap).sort((a, b) => b.count - a.count).slice(0, 10),
    };
  }, [all]);

  const list = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = all.filter((e) => {
      if (who === 'signed' && e.user.key === ANON.key) return false;
      if (who === 'anonymous' && e.user.key !== ANON.key) return false;
      if (who === 'empty' && e.results !== 0) return false;
      return !q || `${e.user.name} ${e.user.email || ''} ${e.dancers.join(' ')}`.toLowerCase().includes(q);
    });
    filtered.sort((a, b) => ((a.date?.getTime() || 0) - (b.date?.getTime() || 0)) * (order === 'newest' ? -1 : 1));
    return filtered;
  }, [all, search, who, order]);

  const filterBy = (text) => { setSearch(text); setShown(PAGE_SIZE); document.getElementById('vs-all-comparisons')?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  if (loading) return <div className="stats-section vs-tab"><div className="loading-message">Loading comparisons…</div></div>;

  if (error) {
    return (
      <div className="stats-section vs-tab">
        <h2>⚔️ Dancer comparisons</h2>
        <div className="vs-alert" role="alert">
          <p>{describeLoadError(error, 'dancerComparisons')}</p>
          <button type="button" className="vs-btn" onClick={onRetry}>Retry</button>
        </div>
      </div>
    );
  }

  if (all.length === 0) {
    return (
      <div className="stats-section vs-tab">
        <h2>⚔️ Dancer comparisons</h2>
        <div className="vs-empty-box">
          No comparisons recorded in this period. They appear here as soon as someone compares dancers on the Compare Dancers page.
        </div>
      </div>
    );
  }

  return (
    <div className="vs-tab">
      <div className="stats-section">
        <h2>⚔️ Dancer comparisons</h2>
        <div className="vs-kpis">
          <div className="vs-kpi"><b>{all.length.toLocaleString()}</b><span>Comparisons</span></div>
          <div className="vs-kpi"><b>{summary.uniqueUsers.toLocaleString()}</b><span>Signed-in users</span></div>
          <div className="vs-kpi"><b>{summary.anonymous.toLocaleString()}</b><span>Anonymous</span></div>
          <div className="vs-kpi"><b>{summary.pairs.toLocaleString()}</b><span>Unique pairs</span></div>
          <div className="vs-kpi"><b>{summary.empty.toLocaleString()}</b><span>With no shared result</span></div>
        </div>
      </div>

      <div className="vs-two-col">
        <div className="stats-section">
          <h2>🔥 Most compared pairs</h2>
          <ol className="vs-rank">
            {summary.topPairs.map(([pair, info], i) => (
              <li key={pair}>
                <span className="vs-rank-n">{i + 1}</span>
                <button type="button" className="vs-rank-main" onClick={() => filterBy(pair.split(' vs ')[0])}>
                  <span className="vs-rank-name">{pair}</span>
                  <span className="vs-rank-sub">{plural(info.users.size, 'user')}</span>
                </button>
                <span className="vs-rank-count">{info.count}×</span>
              </li>
            ))}
          </ol>
        </div>
        <div className="stats-section">
          <h2>🏅 Most active users</h2>
          <ol className="vs-rank">
            {summary.topUsers.map((u, i) => (
              <li key={u.user.key}>
                <span className="vs-rank-n">{i + 1}</span>
                <button type="button" className="vs-rank-main" onClick={() => filterBy(u.user.email || u.user.name)}>
                  <span className="vs-rank-name">{u.user.name}</span>
                  {u.user.email && u.user.email !== u.user.name && <span className="vs-rank-sub">{u.user.email}</span>}
                </button>
                <span className="vs-rank-count">{u.count}×</span>
              </li>
            ))}
            {summary.topUsers.length === 0 && <li className="vs-empty">Only anonymous comparisons so far.</li>}
          </ol>
        </div>
      </div>

      <div className="stats-section" id="vs-all-comparisons">
        <h2>🗒️ All comparisons ({list.length.toLocaleString()})</h2>
        <div className="vs-toolbar">
          <input
            type="search"
            className="vs-input"
            placeholder="Search user or dancer…"
            aria-label="Search comparisons"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setShown(PAGE_SIZE); }}
          />
          <select className="vs-input vs-select" aria-label="Filter comparisons" value={who} onChange={(e) => { setWho(e.target.value); setShown(PAGE_SIZE); }}>
            <option value="all">Everyone</option>
            <option value="signed">Signed-in only</option>
            <option value="anonymous">Anonymous only</option>
            <option value="empty">No shared result</option>
          </select>
          <select className="vs-input vs-select" aria-label="Sort comparisons" value={order} onChange={(e) => setOrder(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
          </select>
        </div>

        <div className="vs-event-list">
          {list.slice(0, shown).map((e) => <ComparisonCard key={e.id} event={e} />)}
          {list.length === 0 && <div className="vs-empty-box">No comparisons match these filters.</div>}
        </div>
        {shown < list.length && (
          <button type="button" className="vs-more" onClick={() => setShown(shown + PAGE_SIZE)}>
            Show {Math.min(PAGE_SIZE, list.length - shown)} more <span>({list.length - shown} hidden)</span>
          </button>
        )}
      </div>
    </div>
  );
}
